/**
 * What `wayai knowledge migrate` writes, decided before anything is written (#5142 step 4).
 *
 * - **File types (K1, K7, K17).** An org resource becomes the file type named by its slug; a hub
 *   resource becomes `<hub-slug>--<resource-slug>`, the hub's part derived from its name. A
 *   resource slug cannot hold `--`, so a hub's file type never takes an org one's id. Each file type
 *   says which resource it came from (its schema's `$comment`), so a resource renamed after its
 *   move finds its file type again, under the id it was given: the id is frozen. A file type
 *   production already holds is never written again (#5083 decision 126): its name, description,
 *   limits and schema stay as they are, an edit made after the move included, and a new file its
 *   schema refuses stops the run. A toolset names a file type's tools `<op>_<name ?? id>` in at most 64
 *   characters, so an id past {@link MAX_TOOL_BASE_LENGTH} characters is named by a short alias,
 *   which a toolset entry takes as its own `name`.
 * - **Schema (K2, K21).** The folder metadata schema is folded into the file metadata schema, beside
 *   `title` and `tags`. On a first move, every metadata key a file carries is declared, so a
 *   metadata filter on it is answered rather than refused, and a declared field that would refuse a
 *   value it holds is widened to accept it: no value is dropped. A later run leaves a moved file
 *   type's schema as production holds it (#5083 decision 126), so a key a later file adds stays
 *   undeclared, and a filter on it is refused, until an admin declares it. The schemas move with
 *   plain local refs only (`#/$defs/<name>`, `#/definitions/<name>`); anything else stops the run
 *   (#5083 decision 120).
 * - **Files (K2).** Each file keeps its path. Its metadata is its own, over its folders' (the
 *   nearest folder winning), with its title and tags. A described folder gains an `_index.md`
 *   holding the description.
 *
 * Pure: `planDesired` takes the reduced resources and what production holds, and returns the file
 * types, the files and the reasons the migration must stop.
 */

import { createHash } from 'node:crypto';
import { isDeepStrictEqual } from 'node:util';
import {
  Validator,
  encodePointer,
  escapePointer,
  ignoredKeyword,
  schemaArrayKeyword,
  schemaMapKeyword,
  validate,
  type Schema,
} from '@cfworker/json-schema';
import { FILE_METADATA_KEYS } from '@wayai/core';
import { TOOLSET_FILE_OPERATIONS } from '@wayai/core/contracts/rekor-toolset';
import {
  folderChain,
  folderPaths,
  type ContentRef,
  type Metadata,
  type SourceFolder,
  type SourceResource,
} from './source.js';

/**
 * The longest file type id a toolset's default tool names fit: a tool name is at most 64
 * characters, and a file tool is named `<op>_<id>` for its longest operation (`delete_<id>`).
 */
export const MAX_TOOL_BASE_LENGTH = 64 - Math.max(...TOOLSET_FILE_OPERATIONS.map((op) => `${op}_`.length));

/** A folder's description, as the file agents read it. */
export const INDEX_FILE = '_index.md';

/** How a file type's schema says which resource it came from (`$comment: <prefix><source key>`). */
export const PROVENANCE_PREFIX = 'wayai knowledge migrate: ';

/** A file type's name may be at most this long. */
const MAX_NAME_LENGTH = 256;

/** The longest path a file takes. */
const MAX_PATH_LENGTH = 1024;

/** The schema `title` and `tags` take, so a search by them keeps working. */
const TITLE_SCHEMA = { type: 'string' };
const TAGS_SCHEMA = { type: 'array', items: { type: 'string' } };

export interface FileTypeSpec {
  id: string;
  name: string;
  description: string;
  max_size: number;
  allowed_content_types: string[];
  metadata_schema: Record<string, unknown>;
}

/** The body a file is written with: a source file's bytes, or a folder's description. */
export type FileBody = { kind: 'source'; ref: ContentRef } | { kind: 'text'; text: string };

export interface DesiredFile {
  fileType: string;
  /** The name of the resource it comes from, for the stops that name it. */
  resource: string;
  path: string;
  contentType: string;
  /** Its size in bytes, when the source row states it. */
  size: number | null;
  metadata: Metadata;
  body: FileBody;
}

/** A schema field the migration widened or declared, as the dry run lists it. */
export interface SchemaNote {
  fileType: string;
  field: string;
  /** `refused`: the field refused a value it holds; `undeclared`: no schema named it; `invalid`: its schema does not compile. */
  reason: 'refused' | 'undeclared' | 'invalid';
  /** The refused values, at most {@link MAX_NOTE_VALUES}, with the file holding each. */
  values: Array<{ path: string; value: unknown }>;
}

const MAX_NOTE_VALUES = 3;

export type Stop =
  | { kind: 'id_collision'; fileType: string; owners: string[] }
  | { kind: 'id_taken'; fileType: string; holder: string | null }
  | { kind: 'source_twice'; source: string; fileTypes: string[] }
  | { kind: 'tool_name_collision'; toolBase: string; fileTypes: string[] }
  | { kind: 'index_exists'; fileType: string; path: string }
  | { kind: 'index_twice'; fileType: string; path: string; folders: string[] }
  | { kind: 'title_clash'; fileType: string; path: string; own: unknown; title: string }
  | { kind: 'tags_clash'; fileType: string; path: string; own: unknown }
  | { kind: 'invalid_path'; fileType: string; path: string; reason: string }
  | { kind: 'limit_refuses'; fileType: string; path: string; why: string }
  | { kind: 'schema_refused'; fileType: string; error: string }
  | { kind: 'unsupported_ref'; fileType: string; resource: string; schema: 'folder' | 'file'; at: string; why: string }
  | { kind: 'definition_clash'; fileType: string; resource: string; definition: string }
  | { kind: 'schema_write_refused'; fileType: string; resource: string; at: string; why: string }
  /** A new file's metadata its file type refuses: one field's value, or (`field` null) the whole metadata. */
  | { kind: 'metadata_refused'; fileType: string; resource: string; path: string; field: string | null; value: unknown; why: string; kept: boolean }
  | { kind: 'hub_slug_empty'; hub: string };

/** Something the migration leaves out, said so the operator knows. */
export type Skip =
  | { kind: 'disabled_files'; fileType: string; count: number }
  | { kind: 'no_content'; fileType: string; path: string }
  | { kind: 'index_without_path'; fileType: string; folder: string };

/** One resource, the file type id it would derive, and the name it would show. */
export interface ResourceTarget {
  resource: SourceResource;
  derivedId: string;
  /** The file type's display name when its id needs no alias. */
  displayName: string;
}

/** A production file type, as the plan reads it: which resource it came from, and the file type itself. */
export interface HeldFileType {
  /** The resource its schema names (`$comment`), or null for a file type this command did not write. */
  source: string | null;
  /** The file type, kept as it is when a resource already has it: a re-run never writes it again. */
  fileType?: {
    name: string;
    description?: string;
    max_size?: number;
    allowed_content_types?: string[];
    metadata_schema?: unknown;
  };
}

export interface DesiredState {
  fileTypes: FileTypeSpec[];
  files: DesiredFile[];
  notes: SchemaNote[];
  stops: Stop[];
  skips: Skip[];
}

export function emptyDesired(stops: Stop[] = []): DesiredState {
  return { fileTypes: [], files: [], notes: [], stops, skips: [] };
}

/** A file type id something other than the planned resources holds, and what holds it. */
export interface ClaimedId {
  id: string;
  owner: string;
}

/** An org resource's file type id: its slug (K1). */
export function orgFileTypeId(resource: SourceResource): string {
  return resource.slug;
}

/** What joins a hub file type id's two slugs (K17): no slug holds it. */
export const HUB_PART_SEPARATOR = '--';

/** A hub resource's file type id: `<hub-slug>--<resource-slug>` (K7, K17). */
export function hubFileTypeId(hubSlug: string, resource: SourceResource): string {
  return `${hubSlug}${HUB_PART_SEPARATOR}${resource.slug}`;
}

/** The hub part of a hub resource's file type id: what {@link hubFileTypeId} took from the hub's name. */
export function hubPartOf(fileTypeId: string): string {
  return fileTypeId.split(HUB_PART_SEPARATOR)[0]!;
}

/**
 * The short name a file type's tools take when its id is too long for them: the id's first 50
 * characters and 6 hex digits of the id's hash, so two long ids sharing a prefix stay apart.
 */
export function toolAlias(id: string): string {
  const hash = createHash('sha256').update(id).digest('hex').slice(0, 6);
  return `${id.slice(0, MAX_TOOL_BASE_LENGTH - hash.length - 1).replace(/-+$/, '')}-${hash}`;
}

/** The base of a file type's tool names: its id, or its alias past {@link MAX_TOOL_BASE_LENGTH}. */
export function toolBase(id: string): string {
  return id.length > MAX_TOOL_BASE_LENGTH ? toolAlias(id) : id;
}

/** A file type's `name`: the alias a toolset entry takes when the id needs one, else its display name. */
export function fileTypeName(id: string, displayName: string): string {
  const base = toolBase(id);
  if (base !== id) return base;
  return displayName.trim().slice(0, MAX_NAME_LENGTH).trim() || id;
}

/** The resource a file type's schema says it came from, or null. */
export function provenanceOf(schema: unknown): string | null {
  const comment = isPlainObject(schema) ? schema.$comment : undefined;
  return typeof comment === 'string' && comment.startsWith(PROVENANCE_PREFIX) ? comment.slice(PROVENANCE_PREFIX.length) : null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A field's schema as the file type takes it: an object's `x-` annotations are a record type's, not a file's. */
function cleanFieldSchema(schema: unknown): unknown {
  if (!isPlainObject(schema)) return schema;
  return Object.fromEntries(Object.entries(schema).filter(([key]) => !key.startsWith('x-')));
}

/** The JSON type of a value, as a schema's `type` names it. */
function jsonType(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'string') return 'string';
  return 'object';
}

/** The root keywords a field's `$ref` can point into, carried with the fields that use them. */
const DEFINITION_KEYS = ['$defs', 'definitions'] as const;

/** A schema's `$defs` and `definitions`, as a root to validate a field against. */
function definitionsOf(root: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(DEFINITION_KEYS.filter((key) => isPlainObject(root[key])).map((key) => [key, root[key]]));
}

// ---- The data product's rules -------------------------------------------------------------
//
// What the data product accepts, decided the way it decides it, so the run stops before its first
// write instead of being refused part-way:
// - metadata schemas are compiled and run with its own validator (`@cfworker/json-schema`'s
//   `Validator`, draft `2020-12`, `shortCircuit: false`) over the schema cleaned as its
//   `compileSchema` cleans it (`apps/rekor/shared/src/validation/schema-validator.ts`:
//   `stripExtensions`, then `guardPatternLengths`), mirrored here because app code may not import
//   the data product's packages (the sealed Rekor tree);
// - a `$ref` resolves when that validator's own index (`dereference`) holds what it names, the
//   lookup a write uses (a source's refs are plain local ones by then: see "Schema refs" below);
// - a file type's write runs more than the compile (`upsertFileType`,
//   `apps/rekor/backend/src/do/file-types.ts`), and each check is predicted (`writeRefusals`):
//   - `validateSchema` with `fieldValidatorsUnsupported` refuses an `x-validate` anywhere its walk
//     (`findNestedValidateDeclarations`) finds one, a file type running no field validators: the
//     fold drops a field's top-level one with its other annotations, and any other stops the run;
//   - its root-field checks read every root field's `x-workflow`, which a `null` field schema
//     throws on: a stop too;
//   - its other checks — `type: object`, root `x-archival`/`x-timezone`, root-field
//     `x-workflow`/`x-search`/`x-fk` — read only keys the fold never carries (the root's own, a
//     field's top-level `x-` keys);
//   - `validateName`, `max_size` and `allowed_content_types` only see a name trimmed to 256
//     characters, `0` and `[]`;
//   - and a file type production already holds is never written again (#5083 decision 126), so
//     none of these checks ever runs on a kept schema: a new file its schema refuses stops the run
//     instead (`metadataRefusals`), naming the field and the value;
// - limits and paths have no shared code app-side, so they are mirrored rule for rule.

/** The draft the data product validates file metadata under. */
const DRAFT = '2020-12';

/** `MAX_PATTERNED_VALUE_LENGTH` (`apps/rekor/shared/src/toolset/filter-params.ts`): a longer value skips `pattern`. */
const MAX_PATTERNED_VALUE_LENGTH = 1024;

/** Keywords whose value is a subschema, or an array of them (`SUBSCHEMA_KEYS` there). */
const SUBSCHEMA_KEYS = new Set([
  'additionalProperties', 'unevaluatedProperties', 'propertyNames', 'items', 'additionalItems',
  'unevaluatedItems', 'contains', 'contentSchema', 'if', 'then', 'else', 'not', 'allOf', 'anyOf',
  'oneOf', 'prefixItems',
]);

/** Keywords whose value maps names to subschemas (`SUBSCHEMA_MAP_KEYS` there). */
const SUBSCHEMA_MAP_KEYS = new Set(['properties', 'patternProperties', '$defs', 'definitions', 'dependentSchemas', 'dependencies']);

/** `stripExtensions`: the root's display and lifecycle hints, and each field's, are never validated. */
function stripExtensions(schema: Record<string, unknown>): Record<string, unknown> {
  const { 'x-ui': _ui, 'x-archival': _archival, 'x-timezone': _timezone, ...cleaned } = schema;
  if (isPlainObject(cleaned.properties)) {
    cleaned.properties = Object.fromEntries(
      Object.entries(cleaned.properties).map(([key, value]) => {
        if (!isPlainObject(value)) return [key, value];
        const { 'x-ui': _xui, 'x-workflow': _wf, 'x-search': _xs, 'x-fk': _fk, 'x-validate': _xv, ...rest } = value;
        return [key, rest];
      }),
    );
  }
  return cleaned;
}

/** `guardPatternLengths`: a `pattern` runs only against a value up to the cap; a longer one skips it. */
function guardPatternLengths(schema: unknown): unknown {
  if (Array.isArray(schema)) return schema.map(guardPatternLengths);
  if (!isPlainObject(schema)) return schema;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(schema)) {
    if (key === 'pattern' && typeof value === 'string') {
      out[key] = `^[\\s\\S]{${MAX_PATTERNED_VALUE_LENGTH + 1}}|(?:${value})`;
    } else if (SUBSCHEMA_KEYS.has(key)) {
      out[key] = guardPatternLengths(value);
    } else if (SUBSCHEMA_MAP_KEYS.has(key) && isPlainObject(value)) {
      out[key] = Object.fromEntries(
        Object.entries(value).map(([name, sub]) => [name, Array.isArray(sub) && key === 'dependencies' ? sub : guardPatternLengths(sub)]),
      );
    } else {
      out[key] = value;
    }
  }
  return out;
}

/** A metadata schema compiled as the data product compiles it, or why it does not compile. */
export type CompiledSchema = { ok: true; validator: Validator; cleaned: Record<string, unknown> } | { ok: false; error: string };

export function compileMetadataSchema(schema: Record<string, unknown>): CompiledSchema {
  // A copy: the validator's index marks the schema objects it walks.
  const cleaned = guardPatternLengths(stripExtensions(structuredClone(schema))) as Record<string, unknown>;
  try {
    return { ok: true, validator: new Validator(cleaned as Schema, DRAFT, false), cleaned };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/** The validator's own index of the schema, the lookup its `$ref`s resolve through on a write. */
function lookupOf(compiled: Extract<CompiledSchema, { ok: true }>): Record<string, unknown> {
  // Public at runtime (`Validator.lookup`), declared private in the package's types.
  return (compiled.validator as unknown as { lookup: Record<string, unknown> }).lookup;
}

/** The URI a schema's `$ref` is looked up by, as the validator resolves it. */
function refUri(node: Record<string, unknown>): string | null {
  if (typeof node.$ref !== 'string') return null;
  const absolute = (node as { __absolute_ref__?: unknown }).__absolute_ref__;
  return typeof absolute === 'string' ? absolute : node.$ref;
}

/**
 * Whether validating against `node` can reach a `$ref` the validator's index does not hold — the
 * write that reaches it answers an error. Refs are followed through the index, once each.
 */
function reachesDanglingRef(node: unknown, lookup: Record<string, unknown>, seen: Set<unknown>): boolean {
  if (Array.isArray(node)) return node.some((item) => reachesDanglingRef(item, lookup, seen));
  if (!isPlainObject(node) || seen.has(node)) return false;
  seen.add(node);
  const uri = refUri(node);
  if (uri !== null) {
    const target = lookup[uri];
    if (target === undefined || reachesDanglingRef(target, lookup, seen)) return true;
  }
  return Object.entries(node).some(([key, value]) => key !== '$ref' && reachesDanglingRef(value, lookup, seen));
}

/** The fields of a compiled schema whose validation reaches a `$ref` that resolves to nothing. */
export function danglingFields(compiled: Extract<CompiledSchema, { ok: true }>): Set<string> {
  const properties = isPlainObject(compiled.cleaned.properties) ? compiled.cleaned.properties : {};
  const lookup = lookupOf(compiled);
  return new Set(Object.keys(properties).filter((field) => reachesDanglingRef(properties[field], lookup, new Set())));
}

/**
 * Whether a field's value passes the field's own schema, as the whole schema's `properties`
 * validates it (with the whole schema's refs); null when validating it errors.
 */
export function fieldAccepts(compiled: Extract<CompiledSchema, { ok: true }>, field: string, value: unknown): boolean | null {
  const properties = isPlainObject(compiled.cleaned.properties) ? compiled.cleaned.properties : {};
  if (!Object.hasOwn(properties, field)) return true;
  try {
    return validate(value, properties[field] as Schema, DRAFT, lookupOf(compiled) as Record<string, Schema>, false).valid;
  } catch {
    return null;
  }
}

/** The metadata key an error's instance location starts at (`#/<key>/…`, escaped as a pointer), or null for the root. */
function fieldOfLocation(location: string, metadata: Record<string, unknown>): string | null {
  const segment = location.startsWith('#/') ? location.slice(2).split('/')[0] : '';
  if (!segment) return null;
  let key: string;
  try {
    key = decodeURI(segment).replace(/~1/g, '/').replace(/~0/g, '~');
  } catch {
    return null;
  }
  return Object.hasOwn(metadata, key) ? key : null;
}

/**
 * Why the data product would refuse `metadata` under `schema`, as it validates a file's write:
 * the fields whose values it refuses (each with its errors), and `field: null` for what refuses the
 * metadata as a whole (a missing required key, a schema that does not compile). Empty when it
 * would accept it.
 */
export function metadataRefusals(schema: Record<string, unknown>, metadata: Record<string, unknown>): Array<{ field: string | null; why: string }> {
  const compiled = compileMetadataSchema(schema);
  if (!compiled.ok) return [{ field: null, why: `its metadata schema does not compile (${compiled.error})` }];
  let errors: Array<{ keyword: string; instanceLocation: string; error: string }>;
  try {
    const result = compiled.validator.validate(metadata);
    if (result.valid) return [];
    errors = result.errors.filter((e) => e.keyword !== 'properties');
  } catch (err) {
    return [{ field: null, why: err instanceof Error ? err.message.split('\n')[0] : String(err) }];
  }
  const byField = new Map<string | null, string[]>();
  for (const e of errors) {
    const field = fieldOfLocation(e.instanceLocation, metadata);
    byField.set(field, [...(byField.get(field) ?? []), `${e.instanceLocation || '#'}: ${e.error}`]);
  }
  return [...byField].map(([field, whys]) => ({ field, why: whys.slice(0, 3).join('; ') }));
}

/**
 * Why the data product would refuse a file of `contentType` and `size` bytes under a file type's
 * limits, or null. Mirrors `normalizeFileWrite` (`apps/rekor/backend/src/do/file-plane.ts`): a
 * size past a non-zero `max_size`, and a content type outside a non-empty `allowed_content_types`.
 */
export function limitRefusal(
  limits: { max_size: number; allowed_content_types: string[] },
  contentType: string,
  size: number | null,
): string | null {
  if (limits.max_size > 0 && size !== null && size > limits.max_size) {
    return `it takes at most ${limits.max_size} bytes, and this is ${size}`;
  }
  if (limits.allowed_content_types.length > 0 && !limits.allowed_content_types.includes(contentType)) {
    return `it takes only ${limits.allowed_content_types.join(', ')}, and this is ${contentType}`;
  }
  return null;
}

// ---- Schema refs -------------------------------------------------------------------------
//
// A resource's schemas move with their refs only in the plain local form: `#/$defs/<name>` or
// `#/definitions/<name>`, `<name>` needing no escaping in a pointer (no `~` or `/`, and nothing
// `encodeURI` changes). The validator's index keys a definition by `encodePointer(<name>)`, which
// leaves such a name as it is, so such a ref names the same definition under the folded schema's
// root as under its source's. Anything that would resolve elsewhere once folded, or that needs a
// rewrite to resolve at all, stops the run instead, and the operator simplifies the schema by hand
// (#5083 decision 120): any other ref form, an `$id` or `$anchor` (which re-base or name refs), a
// definition name needing escaping, and a definition both schemas name with different content. A
// definition both hold alike is folded once.

/** A spot in a source schema the plain-ref rule refuses, by its path from the schema's root. */
interface RefProblem {
  path: string[];
  why: string;
}

/** Whether a plain ref reaches the definition named `name` by naming it as it is: the index keys it by `name` itself. */
function isPlainName(name: string): boolean {
  try {
    return name !== '' && encodePointer(name) === name;
  } catch {
    return false; // a lone surrogate, which `encodeURI` refuses
  }
}

function isPlainRef(ref: unknown): boolean {
  return (
    typeof ref === 'string' &&
    DEFINITION_KEYS.some((keyword) => ref.startsWith(`#/${keyword}/`) && isPlainName(ref.slice(`#/${keyword}/`.length)))
  );
}

/**
 * Every spot in `schema` the plain-ref rule refuses. Walks exactly what the data product's
 * validator indexes (`dereference` in `@cfworker/json-schema`), annotations included: an `$id` in
 * an `x-` annotation the fold keeps is indexed like any other.
 */
function refProblems(schema: unknown): RefProblem[] {
  const problems: RefProblem[] = [];
  const visit = (node: unknown, path: string[]): void => {
    if (!isPlainObject(node)) return;
    const found = (why: string) => problems.push({ path, why });
    if (Object.hasOwn(node, '$id')) found(`it declares $id ${JSON.stringify(node.$id)}`);
    // The validator reads a draft-4 `id` as an `$id` (`$id || id`: any truthy value, made a URL).
    if (!Object.hasOwn(node, '$id') && node.id) found(`it declares id ${JSON.stringify(node.id)}, read as an $id`);
    if (Object.hasOwn(node, '$anchor')) found(`it declares $anchor ${JSON.stringify(node.$anchor)}`);
    if (Object.hasOwn(node, '$ref') && !isPlainRef(node.$ref)) found(`its $ref ${JSON.stringify(node.$ref)} is not a plain local ref`);
    if (Object.hasOwn(node, '$recursiveRef')) found(`its $recursiveRef ${JSON.stringify(node.$recursiveRef)} is not a plain local ref`);
    for (const [key, value] of Object.entries(node)) {
      if ((ignoredKeyword as Record<string, boolean>)[key]) continue;
      if (Array.isArray(value)) {
        if ((schemaArrayKeyword as Record<string, boolean>)[key]) value.forEach((item, i) => visit(item, [...path, key, String(i)]));
      } else if ((schemaMapKeyword as Record<string, boolean>)[key]) {
        if (isPlainObject(value)) for (const [name, sub] of Object.entries(value)) visit(sub, [...path, key, name]);
      } else {
        visit(value, [...path, key]);
      }
    }
  };
  visit(schema, []);
  if (isPlainObject(schema)) {
    for (const [keyword, defs] of Object.entries(definitionsOf(schema))) {
      for (const name of Object.keys(defs as Record<string, unknown>)) {
        if (!isPlainName(name)) problems.push({ path: [keyword, name], why: 'its name needs escaping in a $ref' });
      }
    }
  }
  return problems;
}

/** The root definitions (`<keyword>/<name>`) both schemas name with different content, compared parsed, never as text. */
function definitionClashes(folderSchema: unknown, fileSchema: unknown): string[] {
  if (!isPlainObject(folderSchema) || !isPlainObject(fileSchema)) return [];
  const fileDefs = definitionsOf(fileSchema);
  const clashes: string[] = [];
  for (const [keyword, defs] of Object.entries(definitionsOf(folderSchema))) {
    const other = fileDefs[keyword];
    if (!isPlainObject(other)) continue;
    for (const [name, definition] of Object.entries(defs as Record<string, unknown>)) {
      if (Object.hasOwn(other, name) && !isDeepStrictEqual(definition, other[name])) clashes.push(`${keyword}/${name}`);
    }
  }
  return clashes;
}

/** Where a problem sits, as a stop names it: a field, a definition, the root, or a JSON pointer. */
function locationOf(path: string[]): string {
  if (path.length === 0) return 'the root';
  if (path.length > 1 && path[0] === 'properties') return `field ${JSON.stringify(path[1])}`;
  if (path.length > 1 && (DEFINITION_KEYS as readonly string[]).includes(path[0])) return `definition ${JSON.stringify(`${path[0]}/${path[1]}`)}`;
  return `at ${JSON.stringify(`/${path.map(escapePointer).join('/')}`)}`;
}

/**
 * What a file type's write refuses in `schema` beyond compiling it, as `validateSchema` with
 * `fieldValidatorsUnsupported` refuses it: a root field whose schema is `null`, and an `x-validate`
 * wherever its `findNestedValidateDeclarations` walk reaches (root fields included: a file type
 * runs no field validators). Walked on the schema as the wire carries it.
 */
function writeRefusals(schema: Record<string, unknown>): RefProblem[] {
  const wire = JSON.parse(JSON.stringify(schema)) as Record<string, unknown>;
  const problems: RefProblem[] = [];
  for (const [field, value] of Object.entries(isPlainObject(wire.properties) ? wire.properties : {})) {
    if (value === null) problems.push({ path: ['properties', field], why: 'its schema is null' });
  }
  const visit = (node: unknown, path: string[]): void => {
    if (Array.isArray(node)) {
      node.forEach((item, i) => visit(item, [...path, String(i)]));
      return;
    }
    if (!isPlainObject(node)) return;
    if (node['x-validate'] !== undefined) problems.push({ path, why: 'it declares x-validate' });
    for (const [key, value] of Object.entries(node)) {
      if (SUBSCHEMA_KEYS.has(key)) visit(value, [...path, key]);
      else if (SUBSCHEMA_MAP_KEYS.has(key) && isPlainObject(value)) {
        for (const [name, sub] of Object.entries(value)) visit(sub, [...path, key, name]);
      }
    }
  };
  visit(wire, []);
  return problems;
}

/** A location as {@link locationOf} names it, with its JSON pointer when it sits below a field or definition. */
function preciseLocationOf(path: string[]): string {
  const named = path.length > 2 && (path[0] === 'properties' || (DEFINITION_KEYS as readonly string[]).includes(path[0]));
  return named ? `${locationOf(path)} at ${JSON.stringify(`/${path.map(escapePointer).join('/')}`)}` : locationOf(path);
}

/** Why a resource's schemas cannot be folded as they are: every spot the plain-ref rule refuses. */
function refStops(resource: SourceResource, fileTypeId: string): Stop[] {
  const owner = { fileType: fileTypeId, resource: resource.name };
  const stops: Stop[] = [];
  for (const [schema, source] of [['folder', resource.folderSchema], ['file', resource.fileSchema]] as const) {
    for (const { path, why } of refProblems(source)) stops.push({ kind: 'unsupported_ref', ...owner, schema, at: locationOf(path), why });
  }
  for (const definition of definitionClashes(resource.folderSchema, resource.fileSchema)) {
    stops.push({ kind: 'definition_clash', ...owner, definition });
  }
  return stops;
}

/**
 * The folded schema (K2), as its root keywords and its fields: the provenance, both sources'
 * `$defs` and `definitions` (a name both define holds the same definition in each, or
 * {@link refStops} stopped the run), and the folder schema's fields, the file schema's over them,
 * then `title` and `tags`. Fields are a Map, not an object: a field named `__proto__` is a field
 * like any other.
 */
function foldedSchema(resource: SourceResource): { root: Record<string, unknown>; fields: Map<string, unknown> } {
  const root: Record<string, unknown> = { $comment: `${PROVENANCE_PREFIX}${resource.sourceKey}`, type: 'object' };
  const fields = new Map<string, unknown>();
  for (const schema of [resource.folderSchema, resource.fileSchema]) {
    if (!isPlainObject(schema)) continue;
    for (const key of DEFINITION_KEYS) {
      const defs = schema[key];
      if (isPlainObject(defs)) root[key] = { ...(isPlainObject(root[key]) ? root[key] : {}), ...defs };
    }
    const properties = isPlainObject(schema.properties) ? schema.properties : {};
    for (const [field, fieldSchema] of Object.entries(properties)) fields.set(field, cleanFieldSchema(fieldSchema));
  }
  fields.set('title', TITLE_SCHEMA);
  fields.set('tags', TAGS_SCHEMA);
  return { root, fields };
}

/**
 * A first move's file type schema: the resource's folded schema, every field declared and widened
 * to accept the values `files` hold (K21), with what was widened. A file type production already
 * holds is never folded again (#5083 decision 126).
 */
export function foldSchema(
  resource: SourceResource,
  fileTypeId: string,
  files: Array<{ path: string; metadata: Metadata }>,
): { schema: Record<string, unknown>; notes: SchemaNote[] } {
  const { root, fields } = foldedSchema(resource);
  const notes = new Map<string, SchemaNote>();
  const refusedTypes = new Map<string, Set<string>>();
  const note = (field: string, reason: SchemaNote['reason']): SchemaNote => {
    let entry = notes.get(field);
    if (!entry) {
      entry = { fileType: fileTypeId, field, reason, values: [] };
      notes.set(field, entry);
    }
    return entry;
  };
  const assemble = () => ({ ...root, properties: Object.fromEntries(fields) });
  const widenToAny = (field: string) => {
    fields.set(field, {});
    note(field, 'invalid');
  };

  // A field that keeps the schema from compiling, or whose validation reaches a `$ref` the
  // validator's index cannot resolve, accepts any value instead: the data product would refuse the
  // schema, or answer every write carrying the field with an error.
  let compiled = compileMetadataSchema(assemble());
  if (!compiled.ok) {
    for (const [field, fieldSchema] of fields) {
      if (!compileMetadataSchema({ ...definitionsOf(root), type: 'object', properties: { [field]: fieldSchema } }).ok) widenToAny(field);
    }
    compiled = compileMetadataSchema(assemble());
  }
  if (compiled.ok) {
    const dangling = danglingFields(compiled);
    for (const field of dangling) widenToAny(field);
    if (dangling.size > 0) compiled = compileMetadataSchema(assemble());
  }

  for (const { path, metadata } of files) {
    for (const [field, value] of Object.entries(metadata)) {
      if (!fields.has(field)) {
        fields.set(field, {});
        note(field, 'undeclared');
        continue;
      }
      if (notes.get(field)?.reason === 'invalid') continue;
      const verdict = compiled.ok ? fieldAccepts(compiled, field, value) : null;
      if (verdict === true) continue;
      if (verdict === null) {
        widenToAny(field);
        continue;
      }
      const entry = note(field, 'refused');
      if (entry.values.length < MAX_NOTE_VALUES) entry.values.push({ path, value });
      refusedTypes.set(field, (refusedTypes.get(field) ?? new Set<string>()).add(jsonType(value)));
    }
  }

  // Widened once every value is seen, so a field takes each refused value's type exactly once.
  for (const [field, types] of refusedTypes) {
    if (notes.get(field)?.reason !== 'refused') continue;
    fields.set(field, { anyOf: [fields.get(field), ...[...types].sort().map((type) => ({ type }))] });
  }

  return { schema: assemble(), notes: [...notes.values()] };
}

/** A folder chain's values (nearest first), the nearest folder winning a key two of them hold. */
function folderMetadata(chain: SourceFolder[]): Metadata {
  // Spread, never `Object.assign`: a key named `__proto__` is copied as a value, not as a prototype.
  let values: Metadata = {};
  for (const folder of [...chain].reverse()) values = { ...values, ...folder.metadata };
  return values;
}

/**
 * A file's metadata (K2): its folders' values, the nearest folder winning, then its own over
 * them, then its title and tags. The stored object's own keys (size, type, hash, bucket) are left
 * out: the data product records those of every file itself.
 */
export function fileMetadata(
  own: Metadata,
  title: string,
  tags: string[],
  chain: SourceFolder[],
): { metadata: Metadata } | { clash: 'title' | 'tags'; own: unknown } {
  const ownValues = Object.fromEntries(Object.entries(own).filter(([key]) => !FILE_METADATA_KEYS.has(key)));
  const metadata: Metadata = { ...folderMetadata(chain), ...ownValues };

  const column = title.trim();
  if (column) {
    if (Object.hasOwn(ownValues, 'title') && ownValues.title !== column) return { clash: 'title', own: ownValues.title };
    metadata.title = column;
  }
  if (tags.length > 0) {
    if (Object.hasOwn(ownValues, 'tags')) {
      const ownTags = ownValues.tags;
      if (!Array.isArray(ownTags) || ownTags.some((t) => typeof t !== 'string')) return { clash: 'tags', own: ownTags };
      metadata.tags = [...new Set([...(ownTags as string[]), ...tags])];
    } else {
      metadata.tags = [...tags];
    }
  }
  return { metadata };
}

/**
 * Why the data product would refuse `path` as a file's path, or null. Mirrors `validateFilePath`
 * (`apps/rekor/shared/src/toolset/file-tools.ts`), and refuses rather than lets it trim a path.
 */
function pathRefusal(path: string): string | null {
  if (path !== path.trim()) return 'it begins or ends with whitespace';
  if (path.length > MAX_PATH_LENGTH) return `it is longer than ${MAX_PATH_LENGTH} characters`;
  if (path.split('/').some((segment) => segment === '' || segment === '.' || segment === '..')) {
    return 'it has an empty, "." or ".." segment';
  }
  return null;
}

/** The file types and files one resource becomes. */
function planResource(
  resource: SourceResource,
  fileTypeId: string,
  displayName: string,
  held: HeldFileType | undefined,
  state: DesiredState,
): void {
  // A first move folds the resource's own schemas, which move only with plain local refs. A file
  // type production already holds is never written again, so a re-run never reads its source's.
  const existing = held?.fileType;
  const refs = existing ? [] : refStops(resource, fileTypeId);
  state.stops.push(...refs);

  const byId = new Map(resource.folders.map((folder) => [folder.resource_folder_id, folder]));
  const files: DesiredFile[] = [];

  if (resource.disabledFiles > 0) state.skips.push({ kind: 'disabled_files', fileType: fileTypeId, count: resource.disabledFiles });

  for (const file of resource.files) {
    const refusal = pathRefusal(file.path);
    if (refusal) {
      state.stops.push({ kind: 'invalid_path', fileType: fileTypeId, path: file.path, reason: refusal });
      continue;
    }
    if (!file.content) {
      state.skips.push({ kind: 'no_content', fileType: fileTypeId, path: file.path });
      continue;
    }
    const result = fileMetadata(file.metadata, file.title, file.tags, folderChain(file.folderId, byId));
    if ('clash' in result) {
      state.stops.push(
        result.clash === 'title'
          ? { kind: 'title_clash', fileType: fileTypeId, path: file.path, own: result.own, title: file.title.trim() }
          : { kind: 'tags_clash', fileType: fileTypeId, path: file.path, own: result.own },
      );
      continue;
    }
    files.push({
      fileType: fileTypeId,
      resource: resource.name,
      path: file.path,
      contentType: file.contentType,
      size: file.size,
      metadata: result.metadata,
      body: { kind: 'source', ref: file.content },
    });
  }

  // Every path a source file takes, so an `_index.md` never replaces one (paths that differ only in
  // case are one file to a case-insensitive client, as the path module already treats them). Folder
  // names are not unique, so two described folders can share a path, and one `_index.md` cannot
  // hold both descriptions.
  const taken = new Set(resource.files.map((file) => file.path.toLowerCase()));
  const indexes = new Map<string, SourceFolder[]>();
  const paths = folderPaths(resource.folders);
  for (const folder of resource.folders) {
    if (!(folder.description ?? '').trim()) continue;
    const folderPath = paths.get(folder.resource_folder_id);
    if (!folderPath) {
      state.skips.push({ kind: 'index_without_path', fileType: fileTypeId, folder: folder.folder_name });
      continue;
    }
    const path = `${folderPath}/${INDEX_FILE}`;
    if (taken.has(path.toLowerCase())) {
      state.stops.push({ kind: 'index_exists', fileType: fileTypeId, path });
      continue;
    }
    indexes.set(path.toLowerCase(), [...(indexes.get(path.toLowerCase()) ?? []), folder]);
  }
  for (const [, folders] of indexes) {
    const [folder] = folders;
    const path = `${paths.get(folder.resource_folder_id)}/${INDEX_FILE}`;
    if (folders.length > 1) {
      state.stops.push({ kind: 'index_twice', fileType: fileTypeId, path, folders: folders.map((f) => f.folder_name) });
      continue;
    }
    files.push({
      fileType: fileTypeId,
      resource: resource.name,
      path,
      contentType: 'text/markdown',
      size: new TextEncoder().encode(folder.description ?? '').byteLength,
      metadata: folderMetadata(folderChain(folder.resource_folder_id, byId)),
      body: { kind: 'text', text: folder.description ?? '' },
    });
  }

  // The resource's other stops are reported beside its schema's; its file type is not planned.
  if (refs.length > 0) return;

  if (existing) {
    // A file type production already holds is never written again (#5083 decision 126): its name,
    // description, limits and schema stay as they are, an edit made after the move included. A new
    // file its schema refuses stops the run before any write (`metadataRefusals`).
    state.fileTypes.push({
      id: fileTypeId,
      name: existing.name,
      description: existing.description ?? '',
      max_size: existing.max_size ?? 0,
      allowed_content_types: existing.allowed_content_types ?? [],
      metadata_schema: existing.metadata_schema as Record<string, unknown>,
    });
    state.files.push(...files);
    return;
  }

  const { schema, notes } = foldSchema(resource, fileTypeId, files);
  state.notes.push(...notes);
  const compiled = compileMetadataSchema(schema);
  if (!compiled.ok) state.stops.push({ kind: 'schema_refused', fileType: fileTypeId, error: compiled.error });
  for (const { path, why } of writeRefusals(schema)) {
    state.stops.push({ kind: 'schema_write_refused', fileType: fileTypeId, resource: resource.name, at: preciseLocationOf(path), why });
  }
  state.fileTypes.push({
    id: fileTypeId,
    name: fileTypeName(fileTypeId, displayName),
    description: resource.description,
    max_size: 0,
    allowed_content_types: [],
    metadata_schema: schema,
  });
  state.files.push(...files);
}

/**
 * The file types and files `targets` become, and every reason to stop before writing them: two
 * resources claiming one file type id (K8), an id production holds for another resource, two file
 * types whose tools would share a name, a folder description whose `_index.md` a file already holds
 * (or two such descriptions), a file title or tags its metadata contradicts, a path the data
 * product would refuse, and, on a first move, a schema ref that is not a plain local one.
 *
 * `production` is what production holds, by file type id. A resource whose file type production
 * already holds keeps that id. `claimed` are ids others hold or will hold in the same base (the
 * org's, and, when a hub's are planned, those of another hub whose name derives the same slug: the
 * ids production keeps for it once it has moved, else the ids its name derives).
 * `sharedTools` are the ids whose tools a toolset may hold beside these (the org's, for a hub):
 * their tool names must not collide either.
 */
export function planDesired(
  targets: ResourceTarget[],
  opts: { production?: Map<string, HeldFileType>; claimed?: ClaimedId[]; sharedTools?: string[] } = {},
): DesiredState {
  const state = emptyDesired();
  const production = opts.production ?? new Map<string, HeldFileType>();

  // The file type each resource already has in production, found by the resource it names.
  const bySource = new Map<string, string[]>();
  for (const [id, held] of production) {
    if (held.source) bySource.set(held.source, [...(bySource.get(held.source) ?? []), id]);
  }
  const assigned: Array<ResourceTarget & { fileTypeId: string }> = [];
  for (const target of targets) {
    const kept = bySource.get(target.resource.sourceKey) ?? [];
    if (kept.length > 1) {
      state.stops.push({ kind: 'source_twice', source: target.resource.sourceKey, fileTypes: [...kept].sort() });
      continue;
    }
    const fileTypeId = kept[0] ?? target.derivedId;
    const holder = production.get(fileTypeId);
    if (holder && holder.source !== target.resource.sourceKey) {
      state.stops.push({ kind: 'id_taken', fileType: fileTypeId, holder: holder.source });
      continue;
    }
    assigned.push({ ...target, fileTypeId });
  }

  const owners = new Map<string, string[]>();
  for (const { id, owner } of opts.claimed ?? []) owners.set(id, [...(owners.get(id) ?? []), owner]);
  for (const { resource, fileTypeId } of assigned) {
    owners.set(fileTypeId, [...(owners.get(fileTypeId) ?? []), `resource "${resource.name}" (${resource.id})`]);
  }
  for (const [fileType, claimants] of owners) {
    if (claimants.length > 1 && assigned.some((t) => t.fileTypeId === fileType)) {
      state.stops.push({ kind: 'id_collision', fileType, owners: claimants });
    }
  }

  const byBase = new Map<string, Set<string>>();
  for (const id of [...(opts.sharedTools ?? []), ...assigned.map((t) => t.fileTypeId)]) {
    const base = toolBase(id);
    byBase.set(base, (byBase.get(base) ?? new Set()).add(id));
  }
  for (const [base, ids] of byBase) {
    if (ids.size > 1 && assigned.some((t) => ids.has(t.fileTypeId))) {
      state.stops.push({ kind: 'tool_name_collision', toolBase: base, fileTypes: [...ids] });
    }
  }

  for (const t of assigned) planResource(t.resource, t.fileTypeId, t.displayName, production.get(t.fileTypeId), state);
  return state;
}

/** The sentence a stop is reported with. */
export function describeStop(stop: Stop): string {
  switch (stop.kind) {
    case 'id_collision':
      return `File type "${stop.fileType}" is claimed by ${stop.owners.join(' and ')}. Rename one of them (K8), then run again.`;
    case 'id_taken':
      return stop.holder
        ? `File type "${stop.fileType}" in production holds ${stop.holder}'s knowledge (K8). Rename this resource or its hub, then run again.`
        : `File type "${stop.fileType}" in production was not written by this command. Rename this resource or its hub, then run again.`;
    case 'source_twice':
      return `${stop.source} has two file types in production (${stop.fileTypes.join(', ')}). Remove one, then run again.`;
    case 'tool_name_collision':
      return `File types ${stop.fileTypes.map((id) => `"${id}"`).join(' and ')} would share the tool name base "${stop.toolBase}". Rename one of their resources, then run again.`;
    case 'index_exists':
      return `${stop.fileType}/${stop.path} is a file already, so its folder's description has nowhere to go. Rename that file or clear the folder's description, then run again.`;
    case 'index_twice':
      return `${stop.fileType}/${stop.path} would hold the descriptions of ${stop.folders.length} folders named ${stop.folders.map((f) => `"${f}"`).join(', ')}. Rename one or clear its description, then run again.`;
    case 'title_clash':
      return `${stop.fileType}/${stop.path}: its metadata's title ${JSON.stringify(stop.own)} is not its title "${stop.title}", and one would be lost. Make them agree, then run again.`;
    case 'tags_clash':
      return `${stop.fileType}/${stop.path}: its metadata's tags ${JSON.stringify(stop.own)} are not a list of tags, and its tags would replace them. Make them a list, then run again.`;
    case 'invalid_path':
      return `${stop.fileType}/${stop.path}: a file cannot take this path, because ${stop.reason}. Rename the file, then run again.`;
    case 'limit_refuses':
      return `${stop.fileType}/${stop.path}: ${stop.why}. Change the file or the file type, then run again.`;
    case 'metadata_refused': {
      const what = stop.field === null ? 'its metadata' : `its ${JSON.stringify(stop.field)} is ${JSON.stringify(stop.value)}`;
      const remedy = stop.kept
        ? "A re-run never changes a file type production holds: widen it yourself (in the preview, then promote it) or change the file's metadata, then run again."
        : "Change the file or the resource's schema, then run again.";
      return `File type "${stop.fileType}" (resource "${stop.resource}")${stop.kept ? ' in production' : ''} refuses ${stop.fileType}/${stop.path}: ${what} (${stop.why}). ${remedy}`;
    }
    case 'schema_refused':
      return `File type "${stop.fileType}": its metadata schema does not compile (${stop.error}). Fix the resource's schema, then run again.`;
    case 'unsupported_ref':
      return (
        `File type "${stop.fileType}" (resource "${stop.resource}"), ${stop.schema} metadata schema, ${stop.at}: ${stop.why}. ` +
        'Simplify the schema by hand, then run again: a schema moves only with refs #/$defs/<name> or #/definitions/<name> ' +
        '(a name that needs no escaping), and with no $id, id, $anchor or $recursiveRef.'
      );
    case 'definition_clash':
      return (
        `File type "${stop.fileType}" (resource "${stop.resource}"), folder and file metadata schemas, definition ${JSON.stringify(stop.definition)}: ` +
        'the two schemas define it differently. Make them alike, or rename one and the refs to it, then run again.'
      );
    case 'schema_write_refused':
      return (
        `File type "${stop.fileType}" (resource "${stop.resource}"), metadata schema, ${stop.at}: ${stop.why}, which a file type's write refuses. ` +
        "Change the resource's schema, then run again."
      );
    case 'hub_slug_empty':
      return `Hub "${stop.hub}" has a name with no letters or digits, so its file types have no id. Rename the hub, then run again.`;
  }
}

/** The sentence a skip is reported with. */
export function describeSkip(skip: Skip): string {
  switch (skip.kind) {
    case 'disabled_files':
      return `${skip.fileType}: ${skip.count} disabled file(s) left out — no agent reads them.`;
    case 'no_content':
      return `${skip.fileType}/${skip.path}: left out — it has no stored content.`;
    case 'index_without_path':
      return `${skip.fileType}: folder "${skip.folder}" has a description but no path, so it gets no ${INDEX_FILE}.`;
  }
}
